import { faChevronDown, faCircleNotch, faXmark } from '@fortawesome/pro-regular-svg-icons';
import * as React from 'react';
import { useMemo, useRef, useState } from 'react';

import classnames from '../../utils/classnames.tsx';
import { mergeRefs } from '../../utils/mergeRefs.tsx';
import { useControllableState } from '../../utils/useControllableState.tsx';
import { withIcon } from '../Icon.tsx';
import { createLabelFilterOption, defaultRenderOption, Option, ValueSuggestions } from './AutoCompleteField.tsx';
import Field, { FieldProps } from './Field.tsx';

export interface ComboboxFieldProps
    extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'defaultValue' | 'onChange'>,
        Omit<FieldProps, 'value'> {
    value?: string | null;
    options: Option[];
    renderOption?: (option: Option, value: string) => React.ReactNode;
    emptyMessage?: string;
    isLoading?: boolean;
    popoverMinWidth?: string;
}

const ChevronDownIcon = withIcon(faChevronDown);
const ClearIcon = withIcon(faXmark);
const LoadingCircleIcon = withIcon(faCircleNotch);

// Single select with typeahead: the input shows the selected option's label, the value stays the option value
const ComboboxField = ({
    ref,
    defaultValue,
    value: propsValue,
    onValueChange,
    onCommit,
    onFocus,
    onBlur,
    readOnly,
    disabled,
    required,
    controls,
    className,
    inputRef: propsInputRef = undefined,
    options = [],
    renderOption = defaultRenderOption,
    emptyMessage = undefined,
    isLoading = false,
    popoverMinWidth,
    ...props
}: ComboboxFieldProps) => {
    const inputRef = useRef<HTMLInputElement>(null);

    const [value, setValue] = useControllableState<string | null>(defaultValue ?? '', propsValue, onValueChange);
    const [isOpen, setOpen] = useState(false);
    // null while not typing, the input then shows the selected label
    const [searchText, setSearchText] = useState<string | null>(null);

    const selectedOption = options.find((option) => option.value === value);
    const selectedLabel = selectedOption?.label || value || '';
    const isDirty = searchText !== null;
    const inputValue = searchText ?? selectedLabel;

    const filterOption = useMemo(() => createLabelFilterOption(options), [options]);

    const canOpen = !readOnly && !disabled;

    // every close path (select, escape, blur, outside click) drops unfinished search text
    const handleOpenChange = (open: boolean) => {
        setOpen(open && canOpen);
        if (!open) setSearchText(null);
    };

    const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
        handleOpenChange(true);
        onFocus?.(event);
    };

    const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
        handleOpenChange(false);
        onBlur?.(event);
        onCommit?.(value);
    };

    const handleInputChange = (text: string) => {
        setSearchText(text);
        if (!isOpen) handleOpenChange(true);
    };

    // ValueSuggestions closes the popover and fires onCommit itself
    const handleSelect = (optionValue: string) => {
        setValue(optionValue);
        setSearchText(null);
    };

    const handleClear = () => {
        setValue('');
        setSearchText(null);
    };

    const handleToggle = () => {
        inputRef.current?.focus();
        handleOpenChange(!isOpen);
    };

    return (
        <ValueSuggestions
            inputRef={inputRef}
            value={value || ''}
            onValueChange={handleSelect}
            onCommit={onCommit}
            isOpen={isOpen}
            onOpenChange={handleOpenChange}
            isDirty={isDirty}
            options={options}
            filterOption={filterOption}
            renderOption={renderOption}
            isLoading={isLoading}
            emptyMessage={emptyMessage}
            popoverMinWidth={popoverMinWidth}
        >
            <Field className={classnames('group', className)} readOnly={readOnly} disabled={disabled} ref={ref}>
                <Field.Input>
                    <ValueSuggestions.Input
                        ref={mergeRefs(inputRef, propsInputRef)}
                        value={inputValue}
                        onValueChange={handleInputChange}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        onClick={() => !isOpen && handleOpenChange(true)}
                        readOnly={readOnly}
                        disabled={disabled}
                        {...props}
                    />
                </Field.Input>
                <Field.Controls>
                    {canOpen && (
                        <Field.ControlButton
                            className="group-hover:opacity-100 !bg-surface-transparent"
                            // keep the input focused so the click toggles instead of blur-closing and reopening
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={handleToggle}
                        >
                            {isLoading ? <LoadingCircleIcon spin /> : <ChevronDownIcon />}
                        </Field.ControlButton>
                    )}
                    {!required && canOpen && value && (
                        <Field.ControlButton onClick={handleClear}>
                            <ClearIcon />
                        </Field.ControlButton>
                    )}
                    {controls}
                </Field.Controls>
            </Field>
        </ValueSuggestions>
    );
};

export default ComboboxField;
